import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMenuToggle } from '@/features/super-admin/super-admin-shell-context'
import { useState, useEffect } from 'react'
import { SuperAdminHeader } from '@/features/super-admin/components/SuperAdminHeader'
import { OrganizationFilters } from '@/features/super-admin/components/organizations/OrganizationFilters'
import { OrganizationTable } from '@/features/super-admin/components/organizations/OrganizationTable'
import { CreateOrganizationModal } from '@/features/super-admin/components/organizations/CreateOrganizationModal'
import { SuspendModal } from '@/features/super-admin/components/SuspendModal'
import { ConfirmModal } from '@/features/super-admin/components/ConfirmModal'
import { AlertModal } from '@/features/super-admin/components/AlertModal'
import {
  useOrganizations,
  useCreateOrganization,
  useSuspendOrganization,
  useReactivateOrganization,
  type OrganizationListItem,
  type OrganizationAdminAccess,
} from '@/features/super-admin/hooks/use-organizations'
import { Plus, ChevronLeft, ChevronRight, X, Copy, CheckCircle2 } from 'lucide-react'

type StatusFilter = 'all' | 'active' | 'trial' | 'suspended' | 'past_due' | 'canceled' | 'churned' | 'none'

interface OrganizationFiltersState {
  search?: string
  status?: StatusFilter
  engagement_min?: number
  engagement_max?: number
  trial_expiring_days?: number
}

interface SearchParams {
  status?: string
  trial_expiring_days?: number
  search?: string
}

export const Route = createFileRoute('/super-admin/organizations/')({
  component: OrganizationsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    return {
      status: typeof search.status === 'string' ? search.status : undefined,
      trial_expiring_days:
        typeof search.trial_expiring_days === 'number' ? search.trial_expiring_days : undefined,
      search: typeof search.search === 'string' ? search.search : undefined,
    }
  },
})

function OrganizationsPage() {
  const navigate = useNavigate()
  const { onMenuToggle } = useMenuToggle()
  const searchParams = Route.useSearch()
  const [filters, setFilters] = useState<OrganizationFiltersState>(() => {
    // Initialize from URL search params
    const initial: OrganizationFiltersState = {}
    if (searchParams.status) {
      initial.status = searchParams.status as StatusFilter
    }
    if (searchParams.trial_expiring_days) {
      initial.trial_expiring_days = searchParams.trial_expiring_days
    }
    if (searchParams.search) {
      initial.search = searchParams.search
    }
    return initial
  })
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [suspendingOrg, setSuspendingOrg] = useState<OrganizationListItem | null>(null)
  const [reactivatingOrg, setReactivatingOrg] = useState<OrganizationListItem | null>(null)
  const [createError, setCreateError] = useState<string | null>(null)
  const [createdAdminAccess, setCreatedAdminAccess] = useState<{
    organizationName: string
    access: OrganizationAdminAccess
  } | null>(null)

  // Sync filters with URL search params
  useEffect(() => {
    const newFilters: OrganizationFiltersState = {}
    if (searchParams.status) {
      newFilters.status = searchParams.status as StatusFilter
    }
    if (searchParams.trial_expiring_days) {
      newFilters.trial_expiring_days = searchParams.trial_expiring_days
    }
    if (searchParams.search) {
      newFilters.search = searchParams.search
    }
    setFilters(newFilters)
  }, [searchParams.status, searchParams.trial_expiring_days, searchParams.search])

  const { data, isLoading } = useOrganizations({
    page,
    page_size: 20,
    sort_by: sortBy,
    sort_order: sortOrder,
    ...filters,
  })

  const createMutation = useCreateOrganization()
  const suspendMutation = useSuspendOrganization()
  const reactivateMutation = useReactivateOrganization()

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('desc')
    }
    setPage(1)
  }

  const handleFiltersChange = (newFilters: OrganizationFiltersState) => {
    setFilters(newFilters)
    setPage(1)
  }

  const handleViewDetails = (id: string) => {
    navigate({ to: '/super-admin/organizations/$orgId', params: { orgId: id } })
  }

  const handleCreateOrganization = async (formData: {
    name: string
    billing_email: string
    billing_name?: string
    admin_email: string
    admin_name: string
    plan_id?: string
    skip_trial: boolean
    trial_days?: number
    send_invite: boolean
  }) => {
    try {
      // Generate slug from name
      const slug = formData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      const result = await createMutation.mutateAsync({
        name: formData.name,
        slug,
        plan_id: formData.plan_id,
        billing_email: formData.billing_email,
        billing_name: formData.billing_name,
        skip_trial: formData.skip_trial,
        trial_days: formData.skip_trial ? undefined : formData.trial_days,
        admin_email: formData.admin_email,
        admin_name: formData.admin_name,
        send_invite: formData.send_invite,
      })
      setShowCreateModal(false)
      if (result.admin_access) {
        setCreatedAdminAccess({
          organizationName: result.name,
          access: result.admin_access,
        })
      }
    } catch (error) {
      console.error('Failed to create organization:', error)
      setCreateError(error instanceof Error ? error.message : 'Erro ao criar organização')
    }
  }

  const handleSuspend = (org: OrganizationListItem) => {
    setSuspendingOrg(org)
  }

  const confirmSuspend = async (reason: string) => {
    if (!suspendingOrg) return
    try {
      await suspendMutation.mutateAsync({ id: suspendingOrg.id, data: { reason, notify_admins: true } })
      setSuspendingOrg(null)
    } catch (error) {
      console.error('Failed to suspend organization:', error)
    }
  }

  const handleReactivate = (org: OrganizationListItem) => {
    setReactivatingOrg(org)
  }

  const confirmReactivate = async () => {
    if (!reactivatingOrg) return
    try {
      await reactivateMutation.mutateAsync({ id: reactivatingOrg.id, data: { notify_admins: true } })
      setReactivatingOrg(null)
    } catch (error) {
      console.error('Failed to reactivate organization:', error)
    }
  }

  const pagination = data?.pagination

  return (
    <div className="flex flex-col h-full">
      <SuperAdminHeader
        title="Organizações"
        subtitle={`${pagination?.total || 0} organizações cadastradas`}
        onMenuToggle={onMenuToggle}
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            aria-label="Nova Organização"
            title="Nova Organização"
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nova Organização</span>
          </button>
        }
      />

      <div className="flex-1 p-4 md:p-5 space-y-5 overflow-auto">
        {/* Filters */}
        <OrganizationFilters onFiltersChange={handleFiltersChange} initialFilters={filters} />

        {/* Table */}
        <OrganizationTable
          organizations={data?.data || []}
          loading={isLoading}
          onSort={handleSort}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onViewDetails={handleViewDetails}
          onSuspend={handleSuspend}
          onReactivate={handleReactivate}
        />

        {/* Pagination */}
        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Mostrando {(page - 1) * pagination.page_size + 1} a{' '}
              {Math.min(page * pagination.page_size, pagination.total)} de {pagination.total} organizações
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-gray-600">
                Página {page} de {pagination.total_pages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === pagination.total_pages}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <CreateOrganizationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateOrganization}
        isLoading={createMutation.isPending}
      />

      {/* Suspend Modal */}
      <SuspendModal
        isOpen={!!suspendingOrg}
        onClose={() => setSuspendingOrg(null)}
        onConfirm={confirmSuspend}
        title="Suspender Organização"
        entityName={suspendingOrg?.name || ''}
        isLoading={suspendMutation.isPending}
      />

      {/* Reactivate Confirmation Modal */}
      <ConfirmModal
        isOpen={!!reactivatingOrg}
        onClose={() => setReactivatingOrg(null)}
        onConfirm={confirmReactivate}
        title="Reativar Organização"
        message={`Deseja realmente reativar a organização "${reactivatingOrg?.name}"? Os administradores serão notificados.`}
        confirmText="Reativar"
        variant="info"
        isLoading={reactivateMutation.isPending}
      />

      <AlertModal
        isOpen={!!createError}
        onClose={() => setCreateError(null)}
        title="Erro ao criar organização"
        message={createError || ''}
        variant="error"
      />

      {createdAdminAccess && (
        <OrganizationAdminAccessModal
          organizationName={createdAdminAccess.organizationName}
          access={createdAdminAccess.access}
          onClose={() => setCreatedAdminAccess(null)}
        />
      )}
    </div>
  )
}

function OrganizationAdminAccessModal({
  organizationName,
  access,
  onClose,
}: {
  organizationName: string
  access: OrganizationAdminAccess
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!access.temporary_password) return

    try {
      await navigator.clipboard.writeText(access.temporary_password)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const textArea = document.createElement('textarea')
      textArea.value = access.temporary_password
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Organização criada</h3>
              <p className="text-sm text-gray-500">{organizationName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-2">
            <p className="text-sm text-gray-600">
              <strong>Admin:</strong> {access.email}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Status:</strong>{' '}
              {access.existing_user
                ? 'Usuário existente vinculado à organização'
                : access.invite_sent
                  ? 'Convite enviado por email'
                  : 'Conta criada com senha temporária'}
            </p>
          </div>

          {access.temporary_password ? (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Senha temporária</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-gray-100 rounded-lg font-mono text-sm break-all select-all">
                  {access.temporary_password}
                </code>
                <button
                  onClick={handleCopy}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Copy className={`w-5 h-5 ${copied ? 'text-green-600' : ''}`} />
                </button>
              </div>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                Guarde essa senha e peça para o admin alterá-la no primeiro acesso.
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              {access.invite_sent
                ? 'O admin recebeu um convite por email para acessar a organização.'
                : 'O vínculo do admin com a organização foi confirmado sem necessidade de gerar nova senha.'}
            </p>
          )}
        </div>

        <div className="p-4 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}
